// Path: ./src/app/pages/CreateRolePage/index.tsx
import React, { useEffect, useState } from "react";
import { CreateRoleCard } from "../../components/CreateRoleCard";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { GfPermissionDto } from "@swagger/typescript-fetch-goflyer";
import { useNavigate, useParams } from "react-router-dom";

import { useLoading } from "../../../contextApi/LoadingContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";

type PermissionGroup =
  | "user"
  | "flyer"
  | "coupon"
  | "deals"
  | "upload"
  | "tag"
  | "role"
  | "merchant"
  | "store"
  | "unit"
  | "notification"
  | "flyerScraper"
  | "elasticTrigger"
  | "cache"
  | "image"
  | "feedback"
  | "figmaTemplates"
  | "banner"
  | "figmaComponentLayout"
  | "figmaHealthMonitoring"
  | "smartfly"
  | "imageTag"
  | "analytics";

type Tag =
  | "user"
  | "create"
  | "view"
  | "edit"
  | "delete"
  | "flyer"
  | "menu"
  | "upload"
  | "tag"
  | "role"
  | "merchant"
  | "store"
  | "unit"
  | "notification"
  | "flyerScraper"
  | "elasticTrigger"
  | "cache"
  | "image"
  | "feedback"
  | "figmaTemplates"
  | "banner"
  | "figmaComponentLayout"
  | "figmaHealthMonitoring"
  | "smartfly"
  | "review"
  | "update"
  | "analytics"
  | "filter";

const CreateRolePage = () => {
  const navigate = useNavigate();
  const { showLoadingScreen } = useLoading();
  const { roleId } = useParams();
  const { t } = useTranslation();

  const AccountCardData = {
    cardTitle: t(messages.Add_Role()),
    finishButtonText: roleId ? t(messages.Update()) : t(messages.Finish()),
    cancelButtonText: t(messages.Cancel()),
  };

  const [roleName, setRoleName] = useState("");
  // const [permissionsList, setPermissionList] = useState<GfPermissionDto[]>([]);
  const { permissionsList } = usePermissions();
  const [permissions, setPermissions] = useState({
    user: {
      user: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    flyer: {
      flyer: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    coupon: {
      coupon: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    deals: {
      deals: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    upload: { upload: false, flyer: false, menu: false },
    tag: { tag: false, create: false, edit: false, view: false },
    role: {
      role: false,
      create: false,
      view: false,
      edit: false,
    },
    merchant: {
      merchant: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    store: {
      store: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    unit: {
      unit: false,
      view: false,
      delete: false,
    },
    notification: {
      notification: false,
      create: false,
      view: false,
    },
    flyerScraper: {
      flyerScraper: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    elasticTrigger: {
      elasticTrigger: false,
      view: false,
    },
    cache: {
      cache: false,
      view: false,
    },
    feedback: {
      feedback: false,
      view: false,
    },
    image: {
      image: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    figmaTemplates: {
      figmaTemplates: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    banner: {
      banner: false,
      create: false,
      view: false,
      edit: false,
      delete: false,
    },
    figmaComponentLayout: {
      figmaComponentLayout: false,
      view: false,
      edit: false,
    },
    figmaHealthMonitoring: {
      figmaHealthMonitoring: false,
      view: false,
    },
    smartfly: {
      smartfly: false,
      review: false,
    },
    imageTag: {
      imageTag: false,
      update: false,
    },
    analytics: {
      analytics: false,
      view: false,
      filter: false,
    },
  });

  const handlePermissionsUpdate = (
    tag: string,
    value: boolean,
    head: PermissionGroup,
  ) => {
    const headWithView = [
      "user",
      "flyer",
      "coupon",
      "deals",
      "tag",
      "role",
      "merchant",
      "store",
      "unit",
      "notification",
      "flyerScraper",
      "image",
      "figmaTemplates",
      "banner",
      "figmaComponentLayout",
    ];
    if (headWithView.includes(head)) {
      setPermissions((prevPermissions) => {
        const newPermissions: any = { ...prevPermissions };
        newPermissions[head][tag] = value;

        if (
          newPermissions[head].create ||
          newPermissions[head].edit ||
          newPermissions[head].delete
        ) {
          newPermissions[head].view = true;
        }
        if (
          !newPermissions[head].create &&
          !newPermissions[head].edit &&
          !newPermissions[head].delete
        ) {
          if (tag !== "view") {
            newPermissions[head].view = false;
          }
        }

        return newPermissions;
      });
    } else {
      setPermissions({
        ...permissions,
        [head]: {
          ...permissions[head],
          [tag]: value,
        },
      });
    }
  };

  const PermissionRowData1 = [
    {
      dataTestId: "create-role-user-check",
      title: t(messages.User()),
      isChecked: permissions.user.user,
      handleChange: () => {
        handlePermissionsUpdate("user", !permissions.user.user, "user");
      },

      itemsList: [
        {
          isChecked: permissions.user.create,
          dataTestId: "create-role-user-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target?.checked, "user");
          },
        },
        {
          isChecked: permissions.user.view,
          dataTestId: "create-role-user-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target?.checked, "user");
          },
        },
        {
          isChecked: permissions.user.edit,
          dataTestId: "create-role-user-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target?.checked, "user");
          },
        },
        {
          isChecked: permissions.user.delete,
          dataTestId: "create-role-user-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target?.checked, "user");
          },
        },
      ],
    },
    {
      dataTestId: "create-role-flyer-check",
      title: t(messages.Flyer()),
      isChecked: permissions.flyer.flyer,
      handleChange: () => {
        handlePermissionsUpdate("flyer", !permissions.flyer.flyer, "flyer");
      },
      itemsList: [
        {
          isChecked: permissions.flyer.create,
          dataTestId: "create-role-flyer-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target.checked, "flyer");
          },
        },
        {
          isChecked: permissions.flyer.view,
          dataTestId: "create-role-flyer-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "flyer");
          },
        },
        {
          isChecked: permissions.flyer.edit,
          dataTestId: "create-role-flyer-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "flyer");
          },
        },
        {
          isChecked: permissions.flyer.delete,
          dataTestId: "create-role-flyer-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target.checked, "flyer");
          },
        },
      ],
    },
    {
      dataTestId: "create-role-coupon-check",
      title: t(messages.Coupon()),
      isChecked: permissions.coupon.coupon,
      handleChange: () => {
        handlePermissionsUpdate("coupon", !permissions.coupon.coupon, "coupon");
      },
      itemsList: [
        {
          isChecked: permissions.coupon.create,
          dataTestId: "create-role-coupon-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "coupon",
              );
          },
        },
        {
          isChecked: permissions.coupon.view,
          dataTestId: "create-role-coupon-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "coupon");
          },
        },
        {
          isChecked: permissions.coupon.edit,
          dataTestId: "create-role-coupon-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "coupon");
          },
        },
        {
          isChecked: permissions.coupon.delete,
          dataTestId: "create-role-coupon-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "delete",
                value?.target.checked,
                "coupon",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-deals-check",
      title: t(messages.Deals()),
      isChecked: permissions.deals.deals,
      handleChange: () => {
        handlePermissionsUpdate("deals", !permissions.deals.deals, "deals");
      },
      itemsList: [
        {
          isChecked: permissions.deals.create,
          dataTestId: "create-role-deals-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target.checked, "deals");
          },
        },
        {
          isChecked: permissions.deals.view,
          dataTestId: "create-role-deals-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "deals");
          },
        },
        {
          isChecked: permissions.deals.edit,
          dataTestId: "create-role-deals-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "deals");
          },
        },
        {
          isChecked: permissions.deals.delete,
          dataTestId: "create-role-deals-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target.checked, "deals");
          },
        },
      ],
    },
  ];

  const PermissionRowData2 = [
    {
      dataTestId: "create-role-upload-check",
      title: t(messages.Upload()),
      isChecked: permissions.upload.upload,
      handleChange: () => {
        handlePermissionsUpdate("upload", !permissions.upload.upload, "upload");
      },
      itemsList: [
        {
          isChecked: permissions.upload.flyer,
          dataTestId: "create-role-upload-flyer-check",
          title: t(messages.Flyer()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("flyer", value?.target.checked, "upload");
          },
        },
        {
          isChecked: permissions.upload.menu,
          dataTestId: "create-role-upload-menu-check",
          title: t(messages.Menu()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("menu", value?.target.checked, "upload");
          },
        },
      ],
    },
    {
      dataTestId: "create-role-tag-check",
      title: t(messages.Tag()),
      isChecked: permissions.tag.tag,
      handleChange: () => {
        handlePermissionsUpdate("tag", !permissions.tag.tag, "tag");
      },
      itemsList: [
        {
          isChecked: permissions.tag.create,
          dataTestId: "create-role-tag-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              value &&
              handlePermissionsUpdate("create", value.target.checked, "tag");
          },
        },
        {
          isChecked: permissions.tag.view,
          dataTestId: "create-role-tag-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "tag");
          },
        },
        {
          isChecked: permissions.tag.edit,
          dataTestId: "create-role-tag-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "tag");
          },
        },
      ],
    },
    {
      dataTestId: "create-role-role-check",
      title: t(messages.Role()),
      isChecked: permissions.role.role,
      handleChange: () => {
        handlePermissionsUpdate("role", !permissions.role.role, "role");
      },
      itemsList: [
        {
          isChecked: permissions.role.create,
          dataTestId: "create-role-role-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target.checked, "role");
          },
        },
        {
          isChecked: permissions.role.view,
          dataTestId: "create-role-role-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "role");
          },
        },
        {
          isChecked: permissions.role.edit,
          dataTestId: "create-role-role-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "role");
          },
        },
      ],
    },

    {
      dataTestId: "create-role-merchant-check",
      title: t(messages.Merchant()),
      isChecked: permissions.merchant.merchant,
      handleChange: () => {
        handlePermissionsUpdate(
          "merchant",
          !permissions.merchant.merchant,
          "merchant",
        );
      },
      itemsList: [
        {
          isChecked: permissions.merchant.create,
          dataTestId: "create-role-merchant-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "merchant",
              );
          },
        },
        {
          isChecked: permissions.merchant.view,
          dataTestId: "create-role-merchant-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "merchant",
              );
          },
        },
        {
          isChecked: permissions.merchant.edit,
          dataTestId: "create-role-merchant-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "edit",
                value?.target.checked,
                "merchant",
              );
          },
        },
        {
          isChecked: permissions.merchant.delete,
          dataTestId: "create-role-merchant-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "delete",
                value?.target.checked,
                "merchant",
              );
          },
        },
      ],
    },

    {
      dataTestId: "create-role-store-check",
      title: t(messages.Store()),
      isChecked: permissions.store.store,
      handleChange: () => {
        handlePermissionsUpdate("store", !permissions.store.store, "store");
      },
      itemsList: [
        {
          isChecked: permissions.store.create,
          dataTestId: "create-role-store-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target.checked, "store");
          },
        },
        {
          isChecked: permissions.store.view,
          dataTestId: "create-role-store-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "store");
          },
        },
        {
          isChecked: permissions.store.edit,
          dataTestId: "create-role-store-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "store");
          },
        },
        {
          isChecked: permissions.store.delete,
          dataTestId: "create-role-store-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target.checked, "store");
          },
        },
      ],
    },

    {
      dataTestId: "create-role-unit-check",
      title: t(messages.Unit()),
      isChecked: permissions.unit.unit,
      handleChange: () => {
        handlePermissionsUpdate("unit", !permissions.unit.unit, "unit");
      },
      itemsList: [
        {
          isChecked: permissions.unit.view,
          dataTestId: "create-role-unit-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "unit");
          },
        },

        {
          isChecked: permissions.unit.delete,
          dataTestId: "create-role-unit-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target.checked, "unit");
          },
        },
      ],
    },

    {
      dataTestId: "create-role-notification-check",
      title: t(messages.Notifications()),
      isChecked: permissions.notification.notification,
      handleChange: () => {
        handlePermissionsUpdate(
          "notification",
          !permissions.notification.notification,
          "notification",
        );
      },
      itemsList: [
        {
          isChecked: permissions.notification.create,
          dataTestId: "create-role-notification-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "notification",
              );
          },
        },
        {
          isChecked: permissions.notification.view,
          dataTestId: "create-role-notification-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "notification",
              );
          },
        },
      ],
    },

    {
      dataTestId: "create-role-flyerScraper-check",
      title: t(messages.flyer_scraper()),
      isChecked: permissions.flyerScraper.flyerScraper,
      handleChange: () => {
        handlePermissionsUpdate(
          "flyerScraper",
          !permissions.flyerScraper.flyerScraper,
          "flyerScraper",
        );
      },
      itemsList: [
        {
          isChecked: permissions.flyerScraper.create,
          dataTestId: "create-role-flyerScraper-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "flyerScraper",
              );
          },
        },
        {
          isChecked: permissions.flyerScraper.view,
          dataTestId: "create-role-flyerScraper-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "flyerScraper",
              );
          },
        },
        {
          isChecked: permissions.flyerScraper.edit,
          dataTestId: "create-role-flyerScraper-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "edit",
                value?.target.checked,
                "flyerScraper",
              );
          },
        },
        {
          isChecked: permissions.flyerScraper.delete,
          dataTestId: "create-role-flyerScraper-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "delete",
                value?.target.checked,
                "flyerScraper",
              );
          },
        },
      ],
    },

    {
      dataTestId: "create-role-image-check",
      title: t(messages.Image()),
      isChecked: permissions.image.image,
      handleChange: () => {
        handlePermissionsUpdate("image", !permissions.image.image, "image");
      },
      itemsList: [
        {
          isChecked: permissions.image.create,
          dataTestId: "create-role-image-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("create", value?.target.checked, "image");
          },
        },
        {
          isChecked: permissions.image.view,
          dataTestId: "create-role-image-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "image");
          },
        },
        {
          isChecked: permissions.image.edit,
          dataTestId: "create-role-image-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "image");
          },
        },
        {
          isChecked: permissions.image.delete,
          dataTestId: "create-role-image-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("delete", value?.target.checked, "image");
          },
        },
      ],
    },
    {
      dataTestId: "create-role-figmaTemplates-check",
      title: t(messages.Figma_Templates()),
      isChecked: permissions.figmaTemplates.figmaTemplates,
      handleChange: () => {
        handlePermissionsUpdate(
          "figmaTemplates",
          !permissions.figmaTemplates.figmaTemplates,
          "figmaTemplates",
        );
      },
      itemsList: [
        {
          isChecked: permissions.figmaTemplates.create,
          dataTestId: "create-role-figmaTemplates-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "figmaTemplates",
              );
          },
        },
        {
          isChecked: permissions.figmaTemplates.view,
          dataTestId: "create-role-figmaTemplates-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "figmaTemplates",
              );
          },
        },
        {
          isChecked: permissions.figmaTemplates.edit,
          dataTestId: "create-role-figmaTemplates-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "edit",
                value?.target.checked,
                "figmaTemplates",
              );
          },
        },
        {
          isChecked: permissions.figmaTemplates.delete,
          dataTestId: "create-role-figmaTemplates-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "delete",
                value?.target.checked,
                "figmaTemplates",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-banner-check",
      title: t(messages.Banner()),
      isChecked: permissions.banner.banner,
      handleChange: () => {
        handlePermissionsUpdate("banner", !permissions.banner.banner, "banner");
      },
      itemsList: [
        {
          isChecked: permissions.banner.create,
          dataTestId: "create-role-banner-create-check",
          title: t(messages.Create()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "create",
                value?.target.checked,
                "banner",
              );
          },
        },
        {
          isChecked: permissions.banner.view,
          dataTestId: "create-role-banner-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "banner");
          },
        },
        {
          isChecked: permissions.banner.edit,
          dataTestId: "create-role-banner-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("edit", value?.target.checked, "banner");
          },
        },
        {
          isChecked: permissions.banner.delete,
          dataTestId: "create-role-banner-delete-check",
          title: t(messages.Delete()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "delete",
                value?.target.checked,
                "banner",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-figmaComponentLayout-check",
      title: t(messages.Figma_Component_Layout()),
      isChecked: permissions.figmaComponentLayout.figmaComponentLayout,
      handleChange: () => {
        handlePermissionsUpdate(
          "figmaComponentLayout",
          !permissions.figmaComponentLayout.figmaComponentLayout,
          "figmaComponentLayout",
        );
      },
      itemsList: [
        {
          isChecked: permissions.figmaComponentLayout.view,
          dataTestId: "create-role-figmaComponentLayout-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "figmaComponentLayout",
              );
          },
        },
        {
          isChecked: permissions.figmaComponentLayout.edit,
          dataTestId: "create-role-figmaComponentLayout-edit-check",
          title: t(messages.Edit()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "edit",
                value?.target.checked,
                "figmaComponentLayout",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-figmaHealthMonitoring-check",
      title: t(messages.Figma_Health_Monitoring()),
      isChecked: permissions.figmaHealthMonitoring.figmaHealthMonitoring,
      handleChange: () => {
        handlePermissionsUpdate(
          "figmaHealthMonitoring",
          !permissions.figmaHealthMonitoring.figmaHealthMonitoring,
          "figmaHealthMonitoring",
        );
      },
      itemsList: [
        {
          isChecked: permissions.figmaHealthMonitoring.view,
          dataTestId: "create-role-figmaHealthMonitoring-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "figmaHealthMonitoring",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-smartfly-check",
      title: t(messages.Smartfly_Review()),
      isChecked: permissions.smartfly.smartfly,
      handleChange: () => {
        handlePermissionsUpdate(
          "smartfly",
          !permissions.smartfly.smartfly,
          "smartfly",
        );
      },
      itemsList: [
        {
          isChecked: permissions.smartfly.review,
          dataTestId: "create-role-smartfly-review-check",
          title: t(messages.Review()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "review",
                value?.target.checked,
                "smartfly",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-elasticTrigger-check",
      title: t(messages.ELASTIC_TRIGGER()),
      isChecked: permissions.elasticTrigger.elasticTrigger,
      handleChange: () => {
        handlePermissionsUpdate(
          "elasticTrigger",
          !permissions.elasticTrigger.elasticTrigger,
          "elasticTrigger",
        );
      },
      itemsList: [
        {
          isChecked: permissions.elasticTrigger.view,
          dataTestId: "create-role-elasticTrigger-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "elasticTrigger",
              );
          },
        },
      ],
    },

    {
      dataTestId: "create-role-cache-check",
      title: t(messages.CACHE()),
      isChecked: permissions.cache.cache,
      handleChange: () => {
        handlePermissionsUpdate("cache", !permissions.cache.cache, "cache");
      },
      itemsList: [
        {
          isChecked: permissions.cache.view,
          dataTestId: "create-role-cache-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate("view", value?.target.checked, "cache");
          },
        },
      ],
    },

    {
      dataTestId: "create-role-imageTag-check",
      title: t(messages.Image_Tag()),
      isChecked: permissions.imageTag.imageTag,
      handleChange: () => {
        handlePermissionsUpdate(
          "imageTag",
          !permissions.imageTag.imageTag,
          "imageTag",
        );
      },
      itemsList: [
        {
          isChecked: permissions.imageTag.update,
          dataTestId: "create-role-imageTag-update-check",
          title: t(messages.Update()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "update",
                value?.target.checked,
                "imageTag",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-analytics-check",
      title: t(messages.Analytics_Title()),
      isChecked: permissions.analytics.analytics,
      handleChange: () => {
        handlePermissionsUpdate(
          "analytics",
          !permissions.analytics.analytics,
          "analytics",
        );
      },
      itemsList: [
        {
          isChecked: permissions.analytics.view,
          dataTestId: "create-role-analytics-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "analytics",
              );
          },
        },
        {
          isChecked: permissions.analytics.filter,
          dataTestId: "create-role-analytics-filter-check",
          title: t(messages.Filters()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "filter",
                value?.target.checked,
                "analytics",
              );
          },
        },
      ],
    },
    {
      dataTestId: "create-role-feedback-check",
      title: t(messages.FEEDBACK()),
      isChecked: permissions.feedback.feedback,
      handleChange: () => {
        handlePermissionsUpdate(
          "feedback",
          !permissions.feedback.feedback,
          "feedback",
        );
      },
      itemsList: [
        {
          isChecked: permissions.feedback.view,
          dataTestId: "create-role-feedback-view-check",
          title: t(messages.View()),
          onClick: (value?: React.ChangeEvent<HTMLInputElement>) => {
            value &&
              handlePermissionsUpdate(
                "view",
                value?.target.checked,
                "feedback",
              );
          },
        },
      ],
    },
  ];

  const handleInputChange = (
    textValue?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    textValue && setRoleName(textValue?.target.value);
  };

  const fetchAndUpdateRoleData = async (id: string) => {
    showLoadingScreen(true);
    const roleData =
      await adminApiService.getOneBaseGfRoleAdminControllerGfRole(id);

    setRoleName(roleData.name);
    const updatedPermission = permissions;
    roleData.permissions.forEach((perValue) => {
      const [permissionGroup, tag] = perValue.name.split("-") as [
        PermissionGroup,
        Tag,
      ];
      if (updatedPermission[permissionGroup]) {
        (updatedPermission[permissionGroup] as Record<string, boolean>)[
          permissionGroup
        ] = true;
        (updatedPermission[permissionGroup] as Record<string, boolean>)[tag] =
          true;
      }
    });
    setPermissions(updatedPermission);
    showLoadingScreen(false);
  };

  useEffect(() => {
    roleId && fetchAndUpdateRoleData(roleId);
  }, [roleId]);

  interface FilterObjectProps {
    create?: boolean;
    delete?: boolean;
    edit?: boolean;
    user?: boolean;
    view?: boolean;
    tag?: boolean;
    upload?: boolean;
    deals?: boolean;
    coupon?: boolean;
    flyer?: boolean;

    role?: boolean;
    merchant?: boolean;
    store?: boolean;
    unit?: boolean;
    notification?: boolean;
    flyerScraper?: boolean;
    elasticTrigger?: boolean;
    cache?: boolean;
    feedback?: boolean;
    image?: boolean;
    figmaTemplates?: boolean;
    banner?: boolean;
    figmaComponentLayout?: boolean;
    figmaHealthMonitoring?: boolean;
    smartfly?: boolean;
    review?: boolean;
    imageTag?: boolean;
    update?: boolean;
    analytics?: boolean;
    filter?: boolean;
  }
  const handleSubmit = async () => {
    showLoadingScreen(true);

    if (roleName?.trim()?.length) {
      const array = Object.entries(permissions);
      //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) will come back here and see what to do with this. this looks not right to me
      const filterResult = array.flatMap(
        ([key, value]: [string, FilterObjectProps]) => {
          if (!value) return [];

          return Object.entries(value)
            .filter(([, innerValue]) => innerValue)
            .map(
              ([innerKey]) =>
                value[key as PermissionGroup] &&
                permissionsList?.find(
                  (perValue) => perValue.name === `${key}-${innerKey}`,
                ),
            )
            .filter((permission) => permission !== undefined);
        },
      );
      if (filterResult.length) {
        const selectedPermissions = filterResult.flat(1);
        const filteredPermissions: GfPermissionDto[] =
          selectedPermissions.filter(
            (permission): permission is GfPermissionDto => !!permission,
          );
        const body = {
          name: roleName,
          permissions: filteredPermissions,
        }; //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) will come back here since body is identical to role dto, skip for now

        if (roleId) {
          await adminApiService.updateOneBaseGfRoleAdminControllerGfRole(
            body,
            roleId,
          );
        } else {
          await adminApiService.createOneBaseGfRoleAdminControllerGfRole(body);
        }

        navigate(-1);
      }
    }

    showLoadingScreen(false);
  };

  return (
    <SetupPageContainer data-testid="create-role-page">
      <CreateRoleCard
        cardTitle={AccountCardData.cardTitle}
        FinishButtonText={AccountCardData.finishButtonText}
        CancelButtonText={AccountCardData.cancelButtonText}
        PermissionRowData1={PermissionRowData1}
        PermissionRowData2={PermissionRowData2}
        handleInputText={handleInputChange}
        inputTextValue={roleName}
        onClickFinish={handleSubmit}
        disabledFinishButton={roleName?.trim()?.length ? false : true}
        onClickCancel={() => {
          navigate(-1);
        }}
      />
    </SetupPageContainer>
  );
};

export default CreateRolePage;
